class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.interceptCheckout();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
      this.publishCartDrawerViewed();
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
        this.publishCartDrawerViewed();
      }
    });
  }

  // Shopify only sends cart_viewed on /cart, not when the drawer opens. Publish a
  // custom event so the GTM Checkout custom pixel can send view_cart for the drawer
  // too. Only called when the visitor opens the drawer from the cart icon, not when
  // it opens automatically after add to cart. The pixel itself is consent gated.
  publishCartDrawerViewed() {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

    fetch(`${window.routes.cart_url}.js`)
      .then((res) => res.json())
      .then((cart) => {
        if (!cart.item_count) return;

        // /cart.js returns prices in minor units (øre).
        window.Shopify.analytics.publish('cart_drawer_viewed', {
          currency: cart.currency,
          value: cart.total_price / 100,
          cart_id: cart.token,
          items: cart.items.map((item) => ({
            item_id: item.sku ? item.sku : String(item.variant_id),
            item_name: item.product_title,
            item_category: item.product_type,
            item_brand: item.vendor,
            item_variant: String(item.variant_id),
            image: item.image,
            variant_id: String(item.variant_id),
            variant: item.variant_title,
            product_id: String(item.product_id),
            price: item.price / 100,
            quantity: item.quantity,
          })),
        });
      })
      .catch(() => {});
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => { this.classList.add('animate', 'active') });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Preserves each *existing* <elevate-cart-recommendations> element across
    // this section's full innerHTML replace below, instead of letting the
    // freshly parsed ones take their place - previously did the same for
    // Klevu's cart recs div. Elevate's version needs this for a different
    // reason than Klevu did: its connectedCallback() re-fetches based on the
    // cart's current contents every time it (re)connects, and re-fetching is
    // exactly what should happen on a cart change - but swapping in a brand
    // new, not-yet-rendered element would flash the widget empty for the
    // length of that request. Keeping the same instance means its own
    // already-rendered cards stay on screen until the new fetch resolves.
    //
    // There can be more than one instance now (one per enabled algorithm in
    // Theme settings), so they're matched up by data-algorithm - stable
    // across a render since the set of enabled algorithms doesn't change
    // within a page session.
    const existingElevateCartRecs = Array.from(this.querySelectorAll('elevate-cart-recommendations'));

    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    existingElevateCartRecs.forEach((existing) => {
      const replacement = this.querySelector(`elevate-cart-recommendations[data-algorithm="${existing.dataset.algorithm}"]`);
      if (replacement && replacement !== existing && replacement.parentNode) {
        replacement.parentNode.replaceChild(existing, replacement);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  interceptCheckout() {
    this.addEventListener('click', (event) => {
      const checkoutBtn = event.target.closest('#CartDrawer-Checkout');
      if (!checkoutBtn) return;

      const form = document.getElementById('CartDrawer-Form');
      if (!form || !form.hasAttribute('data-mixed-cart')) return;

      event.preventDefault();
      checkoutBtn.setAttribute('disabled', 'disabled');

      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          const updates = {};
          let hasMix = false;
          let hasPreorder = false;

          cart.items.forEach((item) => {
            if (item.selling_plan_allocation) {
              hasPreorder = true;
            }
          });

          if (!hasPreorder) {
            this.submitCheckoutForm(form);
            return;
          }

          cart.items.forEach((item) => {
            if (!item.selling_plan_allocation) {
              updates[item.key] = 0;
              hasMix = true;
            }
          });

          if (!hasMix) {
            this.submitCheckoutForm(form);
            return;
          }

          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          })
            .then(() => {
              this.submitCheckoutForm(form);
            })
            .catch(() => {
              checkoutBtn.removeAttribute('disabled');
              this.submitCheckoutForm(form);
            });
        })
        .catch(() => {
          checkoutBtn.removeAttribute('disabled');
          this.submitCheckoutForm(form);
        });
    });
  }

  submitCheckoutForm(form) {
    // Programmatic form.submit() does not include button name/value,
    // so we inject a hidden input to tell Shopify this is a checkout submission.
    let hiddenCheckout = form.querySelector('input[name="checkout"]');
    if (!hiddenCheckout) {
      hiddenCheckout = document.createElement('input');
      hiddenCheckout.type = 'hidden';
      hiddenCheckout.name = 'checkout';
      hiddenCheckout.value = '';
      form.appendChild(hiddenCheckout);
    }
    form.submit();
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
